import { useRef } from 'react'

/**
 * The ruby in the crest, made pressable.
 *
 * Purely decorative: it changes nothing about the round, which is why it is
 * a button rather than part of the artwork — a player who taps it gets an
 * answer, and one who never notices it loses nothing.
 *
 * The particles are plain elements driven by the Web Animations API rather
 * than a canvas. A burst is eighteen of them for well under a second, and
 * standing a Pixi application over the header to draw that would cost more
 * to keep alive than the effect it renders. Each one removes itself when its
 * animation finishes, so nothing accumulates however hard the gem is pressed.
 */
export function CrestSpark(): React.JSX.Element {
  const hostRef = useRef<HTMLButtonElement>(null)

  const burst = (): void => {
    const host = hostRef.current
    if (!host) return
    // Decorative motion only: someone who asked the OS for less of it still
    // gets the button, it simply does not throw anything.
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return

    for (let i = 0; i < 18; i++) {
      const spark = document.createElement('i')
      spark.className = 'crest-spark__particle'
      host.appendChild(spark)

      // Evenly spread, then jittered: a perfect ring reads as a loading
      // spinner rather than as something breaking loose.
      const angle = (Math.PI * 2 * i) / 18 + (Math.random() - 0.5) * 0.5
      const distance = 22 + Math.random() * 38
      const animation = spark.animate(
        [
          { transform: 'translate(-50%, -50%) scale(1)', opacity: 1 },
          {
            transform:
              `translate(calc(-50% + ${(Math.cos(angle) * distance).toFixed(1)}px), ` +
              `calc(-50% + ${(Math.sin(angle) * distance).toFixed(1)}px)) scale(0.2)`,
            opacity: 0,
          },
        ],
        { duration: 540 + Math.random() * 280, easing: 'cubic-bezier(0.16, 0.8, 0.3, 1)' },
      )
      animation.onfinish = () => spark.remove()
      animation.oncancel = () => spark.remove()
    }
  }

  return (
    <button
      ref={hostRef}
      className="crest-spark"
      type="button"
      aria-label="Spark the crest gem"
      onClick={burst}
    />
  )
}
